import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import MobileViewContainer from "@/Components/MobileViewContainer";
import Navbar from "@/Components/Common/navbar";
import { Button, Container } from "@/Components/Common/Tailwind";
import { useUserStore } from "@/store/store";
import {
  LEGAL_CONTENT,
  LegalKind,
  hasLegalDocumentContent,
} from "./legalDocuments";

const HTML_TAG_PATTERN = /<\/?[a-z][\s\S]*>/i;
const HTML_ESCAPE_MAP: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

function escapeHtml(value: string) {
  return value.replace(/[&<>"']/g, (character) => HTML_ESCAPE_MAP[character]);
}

function normalizeLegalDocumentContent(value: unknown) {
  if (typeof value !== "string") {
    return "";
  }

  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return "";
  }

  if (HTML_TAG_PATTERN.test(trimmedValue)) {
    return value;
  }

  return escapeHtml(value).replace(/\n/g, "<br />");
}

export default function LegalDocumentPage() {
  const navigate = useNavigate();
  const { kind } = useParams<{ kind: LegalKind }>();
  const siteDetail = useUserStore((state) => state.siteDetail);

  const config = kind ? LEGAL_CONTENT[kind] : null;
  const title = config ? siteDetail?.[config.titleKey] || config.fallbackTitle : "법적 문서";
  const rawContent = config ? siteDetail?.[config.contentKey] || "" : "";
  const content = normalizeLegalDocumentContent(rawContent);
  const hasContent = config && kind ? hasLegalDocumentContent(kind, siteDetail) : false;

  React.useEffect(() => {
    if (config && !hasContent) {
      window.location.replace(config.legacyUrl);
    }
  }, [config, hasContent]);

  if (config && !hasContent) {
    return (
      <MobileViewContainer>
        <Navbar />
        <Container fluid className="min-h-screen px-4 py-6 pb-20 bg-white">
          <div className="rounded-3xl border border-gray-100 bg-white p-5 text-center text-sm text-gray-500 shadow-[0_2px_20px_rgba(0,0,0,0.04)]">
            기존 문서로 이동 중입니다.
          </div>
        </Container>
      </MobileViewContainer>
    );
  }

  return (
    <MobileViewContainer>
      <Navbar />
      <Container fluid className="min-h-screen px-4 py-6 pb-20 bg-white">
        <div className="mb-4 flex items-center justify-between gap-3 rounded-3xl border border-gray-100 bg-white p-4 shadow-[0_2px_20px_rgba(0,0,0,0.04)]">
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">Legal</p>
            <h1 className="mb-0 text-xl font-bold text-gray-900">{title}</h1>
          </div>
          <Button type="button" variant="outline" size="sm" className="bg-white" onClick={() => navigate(-1)}>
            돌아가기
          </Button>
        </div>

        <div className="rounded-3xl border border-gray-100 bg-white p-5 shadow-[0_2px_20px_rgba(0,0,0,0.04)]">
          {content ? (
            <div
              className="prose prose-sm max-w-none text-gray-700 [&_li]:leading-7 [&_ol]:pl-5 [&_p]:leading-7 [&_p]:text-gray-700 [&_ul]:pl-5"
              dangerouslySetInnerHTML={{ __html: content }}
            />
          ) : (
            <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-4 py-12 text-center text-sm text-gray-400">
              등록된 문서가 없습니다.
            </div>
          )}
        </div>
      </Container>
    </MobileViewContainer>
  );
}
